import { ACCOUNT_FILTER_FIELDS, ACCOUNT_SORT_FIELDS, ACCOUNT_SEARCH_FIELDS, FilterKey, ROLE_LABELS } from '@repo/types';
import { ACCOUNT_KIND, ACCOUNT_STATE } from '@theailab-dev/parchment';
import type { AccountKind, AccountState } from '@theailab-dev/parchment';
import React, { useState, useEffect } from 'react';
import { HiSearch, HiRefresh } from 'react-icons/hi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TablePageLayout } from '../../components/Layout/TablePageLayout';
import {
  Button,
  Input,
  Card,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useAccounts } from '../../hooks/useAccounts';

/**
 * Admins 컴포넌트 - 관리자 목록 페이지
 */
const Admins: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentLimit = parseInt(searchParams.get('limit') || '20', 10);
  const filterType = (searchParams.get('filterType') as 'state' | 'gender' | '') || '';
  const filterValue = searchParams.get('filterValue') || '';
  const search = searchParams.get('search') || '';

  const [searchQuery, setSearchQuery] = useState(search);

  const getFilterOptions = () => {
    if (!filterType) return [];
    const filterField = ACCOUNT_FILTER_FIELDS.find((field) => field.key === filterType);
    if (!filterField) return [];
    return filterField.values.map((fv) => ({
      value: fv.value,
      label: fv.value,
    }));
  };

  // 관리자 콘솔 계정만 필터링 (ADMIN_ROLES에 해당하는 유형)
  const getFilterKey = (): FilterKey | undefined => {
    if (search) return FilterKey.SEARCH;
    if (filterType && filterValue) return filterType as FilterKey;
    // 기본값: kind 필터로 관리자 계정만 조회
    return FilterKey.KIND;
  };

  const getFilterValue = (): string | undefined => {
    if (search) return search;
    if (filterType && filterValue) return filterValue;
    return 'ADMIN,OPERATOR,OFFICER';
  };

  const { data, isLoading, isError, error, refetch } = useAccounts({
    page: currentPage,
    limit: currentLimit,
    filterKey: getFilterKey(),
    filterValue: getFilterValue(),
  });

  // 디버깅: 실제 요청 파라미터 확인
  console.log('🔍 관리자 목록 조회 파라미터:', {
    filterKey: getFilterKey(),
    filterValue: getFilterValue(),
    search,
    filterType,
    filterValueFromUrl: filterValue,
  });

  useEffect(() => {
    setSearchQuery(search);
  }, [search, filterValue]);

  useEffect(() => {
    if (!searchParams.has('page')) {
      setSearchParams({ page: '1' });
    }
  }, []);

  const updateUrlParams = (params: {
    page?: number;
    limit?: number;
    filterType?: string;
    filterValue?: string;
    search?: string;
  }) => {
    const newParams = new URLSearchParams(searchParams);
    if (params.page !== undefined) newParams.set('page', params.page.toString());
    if (params.limit !== undefined) newParams.set('limit', params.limit.toString());
    if (params.filterType !== undefined) {
      if (params.filterType) {
        newParams.set('filterType', params.filterType);
      } else {
        newParams.delete('filterType');
      }
    }
    if (params.filterValue !== undefined) {
      if (params.filterValue) {
        newParams.set('filterValue', params.filterValue);
      } else {
        newParams.delete('filterValue');
      }
    }
    if (params.search !== undefined) {
      if (params.search) {
        newParams.set('search', params.search);
      } else {
        newParams.delete('search');
      }
    }
    setSearchParams(newParams);
  };

  const getStateBadgeVariant = (state: AccountState) => {
    switch (state) {
      case ACCOUNT_STATE.NORMAL:
        return 'success';
      case ACCOUNT_STATE.DELETED:
        return 'danger';
      default:
        return 'neutral';
    }
  };

  const getKindBadgeVariant = (kind: AccountKind) => {
    switch (kind) {
      case ACCOUNT_KIND.USER:
        return 'neutral';
      case ACCOUNT_KIND.PARTNER:
        return 'cyan';
      case ACCOUNT_KIND.INSTRUCTOR:
        return 'teal';
      case ACCOUNT_KIND.OFFICER:
        return 'orange';
      case ACCOUNT_KIND.OPERATOR:
        return 'primary';
      case ACCOUNT_KIND.ADMIN:
        return 'purple';
      case ACCOUNT_KIND.SYSTEM:
        return 'danger';
      default:
        return 'neutral';
    }
  };

  const getStateLabel = (state: AccountState) => {
    switch (state) {
      case ACCOUNT_STATE.NORMAL:
        return '정상';
      case ACCOUNT_STATE.DELETED:
        return '삭제됨';
      default:
        return state;
    }
  };

  const handlePageChange = (page: number) => {
    updateUrlParams({ page });
  };

  const handleLimitChange = (newLimit: number) => {
    updateUrlParams({ limit: newLimit, page: 1 });
  };

  const accounts = data?.data || [];
  const meta = data?.meta;
  const total = meta?.total || 0;
  const totalPages = meta?.totalPages || 1;

  if (isError) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-bold text-slate-900">오류가 발생했습니다</h3>
          <p className="mb-6 text-slate-500">
            {error instanceof Error ? error.message : '데이터를 불러오는 중 오류가 발생했습니다.'}
          </p>
          <Button onClick={() => refetch()} variant="outline">
            다시 시도
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <TablePageLayout
      title="관리자 목록"
      description={
        total > 0
          ? `총 ${total.toLocaleString()}명의 관리자가 등록되어 있습니다`
          : '시스템에 등록된 관리자 목록을 확인하세요'
      }
      filters={
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[200px] flex-1">
              <Input
                type="text"
                placeholder={`${ACCOUNT_SEARCH_FIELDS.map(
                  (field) => ACCOUNT_SORT_FIELDS.find((sf) => sf.field === field)?.label || field,
                ).join(', ')}로 검색...`}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  updateUrlParams({
                    page: 1,
                    search: e.target.value,
                    filterType: '',
                    filterValue: '',
                  });
                }}
                leftIcon={<HiSearch />}
              />
            </div>
            <div className="flex gap-2">
              <select
                value={filterType}
                onChange={(e) => {
                  const newType = e.target.value as 'state' | 'gender' | '';
                  updateUrlParams({
                    page: 1,
                    filterType: newType,
                    filterValue: newType ? '' : '',
                  });
                }}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none">
                <option value="">필터 선택</option>
                {ACCOUNT_FILTER_FIELDS.filter((field) => field.key !== 'kind').map((field) => (
                  <option key={field.key} value={field.key}>
                    {field.label}
                  </option>
                ))}
              </select>
              {filterType && getFilterOptions().length > 0 && (
                <select
                  value={filterValue}
                  onChange={(e) => {
                    updateUrlParams({
                      page: 1,
                      filterValue: e.target.value,
                    });
                  }}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none">
                  <option value="">값 선택</option>
                  {getFilterOptions().map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {(search || (filterType && filterValue)) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  updateUrlParams({
                    page: 1,
                    search: '',
                    filterType: '',
                    filterValue: '',
                  });
                }}
                leftIcon={<HiRefresh />}>
                필터 초기화
              </Button>
            )}
          </div>
        </>
      }
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      totalItems={total}
      limit={currentLimit}
      onLimitChange={handleLimitChange}
      isLoading={isLoading}>
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : accounts.length > 0 ? (
        <Table variant="card">
          <TableHeader sticky={false}>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>이메일</TableHead>
              <TableHead>이름</TableHead>
              <TableHead>유형</TableHead>
              <TableHead>상태</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {accounts.map((account) => (
              <TableRow key={account.id} onClick={() => navigate(`/dashboard/users/${account.id}`)}>
                <TableCell nowrap className="text-slate-500">
                  #{account.id}
                </TableCell>
                <TableCell nowrap>
                  <div className="font-medium text-slate-900">{account.email}</div>
                </TableCell>
                <TableCell nowrap>
                  <div className="text-slate-900">{account.name}</div>
                </TableCell>
                <TableCell nowrap>
                  <Badge variant={getKindBadgeVariant(account.kind)}>{ROLE_LABELS[account.kind] ?? account.kind}</Badge>
                </TableCell>
                <TableCell nowrap>
                  <Badge variant={getStateBadgeVariant(account.state)}>{getStateLabel(account.state)}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-50">
            <svg className="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900">등록된 관리자가 없습니다</h3>
          <p className="mt-1 text-slate-500">검색 조건을 변경하거나 새로운 관리자를 기다려주세요.</p>
        </div>
      )}
    </TablePageLayout>
  );
};

export default Admins;
